import React, { useEffect, useState, useMemo } from "react";
import { Row, Col, Card, CardBody, CardTitle, Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Spinner, Alert } from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { pb } from "../../../helpers/pocketbase_helper";
import Switch from "react-switch";
import TableContainer from "../../../components/Common/DataTableContainer";

const SubAcabados = () => {
    document.title = "Subtipos Acabados | GraphicForce - Printesco";

    const [subtipos, setSubtipos] = useState([]);
    const [subtiposInactivos, setSubtiposInactivos] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [modalAdd, setModalAdd] = useState(false);
    const [modalEdit, setModalEdit] = useState(false);

    // Estado de paginación
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [isPagination] = useState(true);
    const [isCustomPageSize] = useState(true);

    const [form, setForm] = useState({ nombre: "" });
    const [editId, setEditId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [formEstado, setFormEstado] = useState(true);

    // For delete confirmation
    const [showDeleteAlert, setShowDeleteAlert] = useState(false);
    const [subtipoToDelete, setSubtipoToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [modalError, setModalError] = useState("");
    
    // For restore confirmation
    const [showRestoreAlert, setShowRestoreAlert] = useState(false);
    const [subtipoToRestore, setSubtipoToRestore] = useState(null);
    const [restoring, setRestoring] = useState(false);

    // Fetch subtipos with pagination
    const fetchSubtipos = async () => {
        try {
            // Obtener todos los subtipos de una vez (como en clientes)
            const allSubtipos = await pb.collection("parametros_acabado_interno").getList(1, 1000, {
                sort: "-created"
            });

            // Separar subtipos activos e inactivos
            const activos = allSubtipos.items.filter(subtipo => subtipo.estado !== false);
            const inactivos = allSubtipos.items.filter(subtipo => subtipo.estado === false);

            // Aplicar paginación solo a los activos
            const startIndex = pageIndex * pageSize;
            const endIndex = startIndex + pageSize;
            const activosPaginados = activos.slice(startIndex, endIndex);

            setSubtipos(activosPaginados);
            setTotalRecords(activos.length);
            setSubtiposInactivos(inactivos);
        } catch (err) {
            console.error("Error fetching subtipos de acabados:", err);
        }
    };

    useEffect(() => {
        fetchSubtipos();
    }, [pageIndex, pageSize]);

    const reloadSubtipos = () => {
        setPageIndex(0); // Reset to first page
        fetchSubtipos();
    };

    // Handlers
    const handleAdd = () => {
        setForm({ nombre: "" });
        setFormEstado(true);
        setModalError("");
        setModalAdd(true);
    };

    const handleEdit = (subtipo) => {
        setForm({ nombre: subtipo.nombre });
        setFormEstado(subtipo.estado !== false);
        setEditId(subtipo.id);
        setModalError("");
        setModalEdit(true);
    };

    const handleRemove = (subtipo) => {
        setSubtipoToDelete(subtipo);
        setShowDeleteAlert(true);
        // Hacer scroll hacia arriba para mostrar el alert
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
    };

    const confirmRemove = async () => {
        if (!subtipoToDelete) return;
        setDeleting(true);
        try {
            const updatedSubtipo = await pb.collection("parametros_acabado_interno").update(subtipoToDelete.id, { estado: false });
            // Remover de la lista de activos y agregar a la de inactivos
            const newSubtipos = subtipos.filter(s => s.id !== subtipoToDelete.id);
            setSubtipos(newSubtipos);
            setSubtiposInactivos([...subtiposInactivos, updatedSubtipo]);
            setTotalRecords(totalRecords - 1);
            
            // Ajustar la página si es necesario
            const newTotalPages = Math.ceil((totalRecords - 1) / pageSize);
            if (pageIndex >= newTotalPages && newTotalPages > 0) {
                setPageIndex(newTotalPages - 1);
            }
            setShowDeleteAlert(false);
            setSubtipoToDelete(null);
            setModalError("");
        } catch (err) {
            setModalError("Error al desactivar subtipo de acabado");
        }
        setDeleting(false);
    };

    const cancelRemove = () => {
        setShowDeleteAlert(false);
        setSubtipoToDelete(null);
    };

    const handleRestore = (subtipo) => {
        setSubtipoToRestore(subtipo);
        setShowRestoreAlert(true);
        // Hacer scroll hacia arriba para mostrar el alert
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
    };

    const confirmRestore = async () => {
        if (!subtipoToRestore) return;
        setRestoring(true);
        try {
            const updatedSubtipo = await pb.collection("parametros_acabado_interno").update(subtipoToRestore.id, { estado: true });
            setSubtiposInactivos(subtiposInactivos.filter(s => s.id !== subtipoToRestore.id));
            setSubtipos([...subtipos, updatedSubtipo]);
            setTotalRecords(totalRecords + 1);
            
            // Si se restauró un elemento, puede que necesitemos ajustar la paginación
            const newTotalPages = Math.ceil((totalRecords + 1) / pageSize);
            if (pageIndex >= newTotalPages && newTotalPages > 0) {
                setPageIndex(newTotalPages - 1);
            }
            setShowRestoreAlert(false);
            setSubtipoToRestore(null);
        } catch (err) {
            setModalError("Error al restaurar subtipo de acabado");
        }
        setRestoring(false);
    };

    const cancelRestore = () => {
        setShowRestoreAlert(false);
        setSubtipoToRestore(null);
    };

    const handleChange = e => {
        const { name, value } = e.target;
        if (name === "nombre") {
            setForm({ ...form, [name]: value.toUpperCase() });
        } else {
            setForm({ ...form, [name]: value });
        }
    };

    const handleSave = async e => {
        e.preventDefault();
        setSaving(true);
        setModalError("");
        try {
            const data = {
                nombre: form.nombre,
                estado: true,
            };
            const newSubtipo = await pb.collection("parametros_acabado_interno").create(data);
            setSubtipos([...subtipos, newSubtipo]);
            setModalAdd(false);
        } catch (err) {
            // Manejo de error de unicidad
            if (
                err?.data?.data?.nombre &&
                err.data.data.nombre.code === "validation_not_unique"
            ) {
                setModalError(
                    `El subtipo de acabado con el nombre "${form.nombre}" ya existe, por favor valide la información e intente de nuevo`
                );
            } else {
                setModalError("Error al agregar subtipo de acabado");
            }
        }
        setSaving(false);
    };

    const handleUpdate = async e => {
        e.preventDefault();
        setSaving(true);
        setModalError("");
        try {
            const data = {
                nombre: form.nombre,
                estado: formEstado,
            };
            const updatedSubtipo = await pb.collection("parametros_acabado_interno").update(editId, data);
            setSubtipos(subtipos.map(s => (s.id === editId ? updatedSubtipo : s)));
            setModalEdit(false);
        } catch (err) {
            // Manejo de error de unicidad
            if (
                err?.data?.data?.nombre &&
                err.data.data.nombre.code === "validation_not_unique"
            ) {
                setModalError(
                    `El subtipo de acabado con el nombre "${form.nombre}" ya existe, por favor valide la información e intente de nuevo`
                );
            } else {
                setModalError("Error al editar subtipo de acabado");
            }
        }
        setSaving(false);
    };

    // --- Filtering and Pagination ---
    const columns = useMemo(() => [
        {
            header: "Nombre",
            accessorKey: "nombre",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Estado",
            accessorKey: "estado",
            cell: info =>
                info.getValue() === false
                    ? <span className="badge bg-danger">Inactivo</span>
                    : <span className="badge bg-success">Activo</span>,
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Acciones",
            accessorKey: "acciones",
            cell: info => {
                const subtipo = info.row.original;
                return (
                    <div style={{ display: "flex", gap: 4, minWidth: 140, maxWidth: 180 }}>
                        <Button color="warning" size="sm" className="me-2" onClick={() => handleEdit(subtipo)}>
                            <i className="fas fa-pen me-1"></i> Editar
                        </Button>
                        <Button color="danger" size="sm" onClick={() => handleRemove(subtipo)} disabled={subtipo.estado === false}>
                            <i className="fas fa-eraser me-1"></i> Eliminar
                        </Button>
                    </div>
                );
            },
            enableColumnFilter: false,
            enableSorting: false,
            meta: { style: { width: "1%", whiteSpace: "nowrap" } },
        },
    ], [subtipos]);

    // Columnas para subtipos inactivos
    const columnsInactivos = useMemo(() => [
        {
            header: "Nombre",
            accessorKey: "nombre",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Estado",
            accessorKey: "estado",
            cell: info =>
                info.getValue() === false
                    ? <span className="badge bg-danger">Inactivo</span>
                    : <span className="badge bg-success">Activo</span>,
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Acciones",
            accessorKey: "acciones",
            cell: info => {
                const subtipo = info.row.original;
                return (
                    <div style={{ display: "flex", gap: 4, minWidth: 140, maxWidth: 180 }}>
                        <Button color="success" size="sm" onClick={() => handleRestore(subtipo)}>
                            <i className="fas fa-undo me-1"></i> Restaurar
                        </Button>
                    </div>
                );
            },
            enableColumnFilter: false,
            enableSorting: false,
            meta: { style: { width: "1%", whiteSpace: "nowrap" } },
        },
    ], [subtiposInactivos]);

    return (
        <React.Fragment>
            <div className="page-content">
                <div className="container-fluid">
                    <Breadcrumbs maintitle="Dashboard" title="Administración" breadcrumbItem="Subtipos Acabados" />

                    {/* Dismissing Alert for Delete */}
                    {showDeleteAlert && subtipoToDelete && (
                        <Alert
                            color="warning"
                            isOpen={showDeleteAlert}
                            toggle={cancelRemove}
                            fade={true}
                            transition={{ timeout: 150 }}
                            className="d-flex align-items-center justify-content-between"
                        >
                            <div>
                                <strong>¿Está seguro?</strong> Esto desactivará el subtipo de acabado <b>{subtipoToDelete.nombre}</b>.
                            </div>
                            <div>
                                <Button color="danger" size="sm" className="me-2" onClick={confirmRemove} disabled={deleting}>
                                    {deleting ? <Spinner size="sm" /> : <i className="fas fa-eraser me-1"></i>} Confirmar
                                </Button>
                                <Button color="secondary" size="sm" onClick={cancelRemove} disabled={deleting}>
                                    Cancelar
                                </Button>
                            </div>
                        </Alert>
                    )}

                    {/* Dismissing Alert for Restore */}
                    {showRestoreAlert && subtipoToRestore && (
                        <Alert
                            color="info"
                            isOpen={showRestoreAlert}
                            toggle={cancelRestore}
                            fade={true}
                            transition={{ timeout: 150 }}
                            className="d-flex align-items-center justify-content-between"
                        >
                            <div>
                                <strong>¿Está seguro?</strong> Esto restaurará el subtipo de acabado <b>{subtipoToRestore.nombre}</b>.
                            </div>
                            <div>
                                <Button color="success" size="sm" className="me-2" onClick={confirmRestore} disabled={restoring}>
                                    {restoring ? <Spinner size="sm" /> : <i className="fas fa-undo me-1"></i>} Confirmar
                                </Button>
                                <Button color="secondary" size="sm" onClick={cancelRestore} disabled={restoring}>
                                    Cancelar
                                </Button>
                            </div>
                        </Alert>
                    )}

                    <Row>
                        <Col>
                            <Card>
                                <CardBody>
                                    <CardTitle className="h4 d-flex justify-content-between align-items-center">
                                        Subtipos Acabados
                                        <div>
                                            <Button color="primary" size="sm" onClick={handleAdd}>
                                                <i className="fas fa-plus me-1"></i> Agregar
                                            </Button>
                                            <Button
                                                color="secondary"
                                                size="sm"
                                                className="ms-2"
                                                title="Recargar tabla"
                                                onClick={reloadSubtipos}
                                            >
                                                <i className="fas fa-sync-alt"></i>
                                            </Button>
                                        </div>
                                    </CardTitle>
                                    <div className="table-rep-plugin">
                                        <div className="table-responsive mb-0" data-pattern="priority-columns">
                                            <TableContainer
                                                columns={columns}
                                                data={subtipos}
                                                isGlobalFilter={false}
                                                isPagination={true}
                                                isCustomPageSize={true}
                                                pageIndex={pageIndex}
                                                setPageIndex={setPageIndex}
                                                pageSize={pageSize}
                                                setPageSize={setPageSize}
                                                totalRecords={totalRecords}
                                                pagination="pagination"
                                                paginationWrapper='dataTables_paginate paging_simple_numbers'
                                                tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                                            />
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* Tabla de Subtipos Inactivos */}
                    {subtiposInactivos.length > 0 && (
                        <Row className="mt-4">
                            <Col>
                                <Card>
                                    <CardBody>
                                        <CardTitle className="h4 d-flex justify-content-between align-items-center">
                                            Subtipos Acabados Inactivos
                                        </CardTitle>
                                        <div className="table-rep-plugin">
                                            <div className="table-responsive mb-0" data-pattern="priority-columns">
                                                <TableContainer
                                                    columns={columnsInactivos}
                                                    data={subtiposInactivos}
                                                    isGlobalFilter={false}
                                                    isPagination={false}
                                                    isCustomPageSize={false}
                                                    tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                                                />
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    )}
                </div>
            </div>

            {/* Modal Agregar */}
            <Modal isOpen={modalAdd} toggle={() => setModalAdd(!modalAdd)} size="lg">
                <ModalHeader toggle={() => setModalAdd(!modalAdd)}>Agregar Subtipo de Acabado</ModalHeader>
                <Form onSubmit={handleSave}>
                    <ModalBody>
                        {modalError && (
                            <Alert
                                color="danger"
                                isOpen={!!modalError}
                                toggle={() => setModalError("")}
                                fade={true}
                                transition={{ timeout: 150 }}
                                className="d-flex align-items-center justify-content-between mb-3"
                            >
                                <div>
                                    <strong>Error:</strong> {modalError}
                                </div>
                            </Alert>
                        )}
                        <FormGroup>
                            <Label for="nombre">Nombre</Label>
                            <Input
                                type="text"
                                name="nombre"
                                id="nombre"
                                value={form.nombre}
                                onChange={handleChange}
                                required
                            />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setModalAdd(false)}>
                            Cancelar
                        </Button>
                        <Button color="primary" type="submit" disabled={saving}>
                            {saving ? <Spinner size="sm" className="me-2" /> : null}
                            {saving ? "Guardando..." : "Guardar"}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>

            {/* Modal Editar */}
            <Modal isOpen={modalEdit} toggle={() => setModalEdit(!modalEdit)} size="lg">
                <ModalHeader toggle={() => setModalEdit(!modalEdit)}>Editar Subtipo de Acabado</ModalHeader>
                <Form onSubmit={handleUpdate}>
                    <ModalBody>
                        {modalError && (
                            <Alert
                                color="danger"
                                isOpen={!!modalError}
                                toggle={() => setModalError("")}
                                fade={true}
                                transition={{ timeout: 150 }}
                                className="d-flex align-items-center justify-content-between mb-3"
                            >
                                <div>
                                    <strong>Error:</strong> {modalError}
                                </div>
                            </Alert>
                        )}
                        <FormGroup>
                            <Label for="nombre_edit">Nombre</Label>
                            <Input
                                type="text"
                                name="nombre"
                                id="nombre_edit"
                                value={form.nombre}
                                onChange={handleChange}
                                required
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Estado</Label>
                            <div>
                                <Switch
                                    checked={formEstado}
                                    onChange={setFormEstado}
                                    onColor="#02a499"
                                    offColor="#ec4561"
                                    uncheckedIcon={
                                        <div
                                            style={{
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                height: "100%",
                                                fontSize: 12,
                                                color: "#fff",
                                                paddingRight: 2
                                            }}
                                        >
                                            No
                                        </div>
                                    }
                                    checkedIcon={
                                        <div
                                            style={{
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                height: "100%",
                                                fontSize: 12,
                                                color: "#fff",
                                                paddingRight: 2
                                            }}
                                        >
                                            Sí
                                        </div>
                                    }
                                />
                            </div>
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setModalEdit(false)}>
                            Cancelar
                        </Button>
                        <Button color="primary" type="submit" disabled={saving}>
                            {saving ? <Spinner size="sm" className="me-2" /> : null}
                            {saving ? "Guardando..." : "Guardar"}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>
        </React.Fragment>
    );
};

export default SubAcabados;
